export type UsuarioParaRiesgo = {
  id: string;
  nombre?: string | null;
  email?: string | null;
  estado?: string | null;
  tipo_membresia?: string | null;
  fecha_vencimiento?: string | null;
  created_at?: string | null;
};

export type AccesoParaRiesgo = {
  usuario_id?: string | null;
  ocurrido_at: string;
};

export type RiesgoFuga = {
  id: string;
  nombre: string;
  email: string;
  plan: string;
  ultimaAsistencia: string | null;
  diasSinAsistir: number;
  asistenciasRecientes: number;
  asistenciasAnteriores: number;
  puntaje: number;
  nivel: 'alto' | 'medio';
  motivos: string[];
};

export function calcularRiesgosFuga(
  usuarios: UsuarioParaRiesgo[],
  accesosRows: AccesoParaRiesgo[],
  hoy = new Date(),
): RiesgoFuga[] {
  const ahora = hoy.getTime();
  const hoyStr = hoy.toISOString().split('T')[0];
  const limiteReciente = ahora - 14 * 86400000;
  const accesosPorUsuario = new Map<string, string[]>();

  accesosRows.forEach((acceso) => {
    if (!acceso.usuario_id) return;
    const lista = accesosPorUsuario.get(acceso.usuario_id) || [];
    lista.push(acceso.ocurrido_at);
    accesosPorUsuario.set(acceso.usuario_id, lista);
  });

  return usuarios
    .filter((usuario) => usuario.estado === 'activo' && (!usuario.fecha_vencimiento || usuario.fecha_vencimiento >= hoyStr))
    .map((usuario): RiesgoFuga | null => {
      const accesosCrudos = accesosPorUsuario.get(usuario.id) || [];
      const accesos = Array.from(
        new Map(
          accesosCrudos.map((fecha) => [
            new Date(fecha).toLocaleDateString('en-CA', { timeZone: 'America/Santiago' }),
            fecha,
          ]),
        ).values(),
      ).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
      const ultimaAsistencia = accesos[0] || null;
      const diasDesdeRegistro = usuario.created_at
        ? Math.floor((ahora - new Date(usuario.created_at).getTime()) / 86400000)
        : 30;
      const diasSinAsistir = ultimaAsistencia
        ? Math.floor((ahora - new Date(ultimaAsistencia).getTime()) / 86400000)
        : Math.max(0, diasDesdeRegistro);
      const asistenciasRecientes = accesos.filter((fecha) => new Date(fecha).getTime() >= limiteReciente).length;
      const asistenciasAnteriores = accesos.length - asistenciasRecientes;
      const motivos: string[] = [];
      let puntaje = 0;

      if (!ultimaAsistencia && diasDesdeRegistro < 7) return null;
      if (!ultimaAsistencia) {
        puntaje += 55;
        motivos.push(`Sin ingresos registrados tras ${diasDesdeRegistro} días`);
      } else if (diasSinAsistir >= 21) {
        puntaje += 55;
        motivos.push(`${diasSinAsistir} días sin asistir`);
      } else if (diasSinAsistir >= 14) {
        puntaje += 40;
        motivos.push(`${diasSinAsistir} días sin asistir`);
      } else if (diasSinAsistir >= 7) {
        puntaje += 25;
        motivos.push(`${diasSinAsistir} días sin asistir`);
      }

      if (asistenciasAnteriores >= 2 && asistenciasRecientes <= asistenciasAnteriores / 2) {
        puntaje += 30;
        motivos.push(`Bajó de ${asistenciasAnteriores} a ${asistenciasRecientes} asistencias quincenales`);
      } else if (asistenciasAnteriores >= 3 && asistenciasRecientes < asistenciasAnteriores) {
        puntaje += 15;
        motivos.push('Disminuyó su frecuencia de asistencia');
      }

      if (usuario.fecha_vencimiento) {
        const diasParaVencer = Math.ceil(
          (new Date(`${usuario.fecha_vencimiento}T23:59:59`).getTime() - ahora) / 86400000,
        );
        if (diasParaVencer <= 7) {
          puntaje += 15;
          motivos.push(`Su plan vence en ${Math.max(0, diasParaVencer)} días`);
        }
      }

      if (puntaje < 40) return null;
      return {
        id: usuario.id,
        nombre: usuario.nombre || 'Sin nombre',
        email: usuario.email || '',
        plan: usuario.tipo_membresia || 'Sin plan',
        ultimaAsistencia,
        diasSinAsistir,
        asistenciasRecientes,
        asistenciasAnteriores,
        puntaje: Math.min(100, puntaje),
        nivel: puntaje >= 70 ? 'alto' : 'medio',
        motivos,
      };
    })
    .filter((item): item is RiesgoFuga => item !== null)
    .sort((a, b) => b.puntaje - a.puntaje);
}
